import { createFileRoute } from "@tanstack/react-router";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, X, Pencil, ArrowRight, Lock, Circle, Plus, Trash2, Flame } from "lucide-react";
import {
  getToday,
  saveDailyEntry,
  deleteDailyEntry,
  saveBoldMove,
  listBoldMoves,
} from "@/lib/momentum.functions";
import { useBusiness } from "@/components/business-context";
import {
  CATEGORIES,
  formatMonth,
  isFirstOfMonthISO,
  monthISO,
  shiftISODate,
  todayISO,
} from "@/lib/momentum-types";
import type { Category } from "@/lib/momentum-types";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { useOfflineFn } from "@/lib/offline";
import WinMoment, { markWin } from "@/components/WinMoment";

export const Route = createFileRoute("/_authenticated/app/today")({
  head: () => ({
    meta: [
      { title: "Today — The Founders App" },
      { name: "description", content: "Set today's moves and log how they went, per business." },
      { property: "og:title", content: "Today — The Founders App" },
      { property: "og:description", content: "Set today's moves and log how they went." },
    ],
  }),
  component: TodayPage,
});

interface TodayEntry {
  id: string;
  morning_text: string;
  when_where: string | null;
  done: boolean | null;
  category: string | null;
  evening_text: string | null;
}

function MoveCard({
  entry,
  index,
  onSave,
  onDelete,
}: {
  entry: TodayEntry;
  index: number;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [morning, setMorning] = useState(entry.morning_text);
  const [whenWhere, setWhenWhere] = useState(entry.when_where ?? "");
  const [category, setCategory] = useState<Category | null>((entry.category as Category) ?? null);
  const [done, setDone] = useState<boolean | null>(entry.done);
  const [evening, setEvening] = useState(entry.evening_text ?? "");
  const [saving, setSaving] = useState(false);
  const [savingEvening, setSavingEvening] = useState(false);

  useEffect(() => {
    setMorning(entry.morning_text);
    setWhenWhere(entry.when_where ?? "");
    setCategory((entry.category as Category) ?? null);
    setDone(entry.done);
    setEvening(entry.evening_text ?? "");
  }, [entry]);

  const closed = Boolean(entry.evening_text?.trim()) || entry.done !== null;

  const saveMorning = async () => {
    if (!morning.trim()) {
      toast.error("Write the move first");
      return;
    }
    setSaving(true);
    try {
      await onSave({ morning_text: morning, when_where: whenWhere, category });
      setEditing(false);
      toast.success("Updated");
    } finally {
      setSaving(false);
    }
  };

  const saveEvening = async () => {
    if (done === null && !evening.trim()) {
      toast.error("Mark it done or write a line");
      return;
    }
    setSavingEvening(true);
    try {
      await onSave({ evening_text: evening, done });
      toast.success("Outcome logged");
      if (done === true) markWin();
    } finally {
      setSavingEvening(false);
    }
  };

  return (
    <section className="rise spotlight vault-panel p-7">
      {editing ? (
        <>
          <p className="micro-label">Move {String(index + 1).padStart(2, "0")}</p>
          <Textarea
            className="mt-4 min-h-24 resize-none"
            value={morning}
            onChange={(e) => setMorning(e.target.value)}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setCategory(category === c.value ? null : c.value)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  category === c.value
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
          <Input
            className="mt-4"
            placeholder="When and where"
            value={whenWhere}
            onChange={(e) => setWhenWhere(e.target.value)}
          />
          <div className="mt-5 flex flex-wrap gap-2">
            <Button className="sheen" disabled={saving} onClick={() => void saveMorning()}>
              {saving ? "Saving…" : "Update this move"}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between gap-4">
            <div className={cn("flex items-center gap-2", closed ? "text-primary" : "text-muted-foreground")}>
              {closed ? (
                <Lock className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Circle className="h-4 w-4" aria-hidden="true" />
              )}
              <span className={cn("micro-label", closed ? "text-primary" : "text-muted-foreground")}>
                Move {String(index + 1).padStart(2, "0")} · {closed ? "locked in" : "in play"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="flex min-h-9 items-center gap-1.5 rounded-sm border border-border px-3 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit
              </button>
              <ConfirmDelete
                ariaLabel="Remove this move"
                itemLabel="this move"
                onConfirm={onDelete}
                className="flex min-h-9 items-center rounded-sm border border-border px-3 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </ConfirmDelete>
            </div>
          </div>
          <p className="notranslate mt-4 text-lg leading-relaxed">{entry.morning_text}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {entry.category && (
              <span className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-foreground">
                {CATEGORIES.find((c) => c.value === entry.category)?.label ?? entry.category}
              </span>
            )}
            {entry.when_where && (
              <span className="notranslate rounded-full border border-border px-3 py-1">{entry.when_where}</span>
            )}
          </div>

          <div className="mt-6 border-t border-border pt-5">
            <div className="flex items-center justify-between gap-4">
              <p className="micro-label">Tonight</p>
              {closed && (
                <span className="flex items-center gap-1.5 text-xs text-primary">
                  <Check className="h-3.5 w-3.5" aria-hidden="true" /> Outcome saved
                </span>
              )}
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => setDone(done === true ? null : true)}
                className={cn(
                  "flex items-center gap-2 rounded-sm border px-3 py-1.5 text-xs transition-colors",
                  done === true
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                <Check className="h-3.5 w-3.5" /> I did it
              </button>
              <button
                type="button"
                onClick={() => setDone(done === false ? null : false)}
                className={cn(
                  "flex items-center gap-2 rounded-sm border px-3 py-1.5 text-xs transition-colors",
                  done === false
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                <X className="h-3.5 w-3.5" /> Not today
              </button>
            </div>
            <Textarea
              className="mt-4 min-h-20 resize-none"
              placeholder="How it went…"
              value={evening}
              onChange={(e) => setEvening(e.target.value)}
            />
            <Button
              variant="secondary"
              className="mt-4"
              disabled={savingEvening}
              onClick={() => void saveEvening()}
            >
              {savingEvening ? "Saving…" : closed ? "Edit outcome" : "Log the outcome"}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

function TodayPage() {
  const { active } = useBusiness();
  const businessId = active?.id ?? "";
  const date = todayISO();
  const month = monthISO();

  const fetchToday = useServerFn(getToday);
  const saveEntry = useOfflineFn("saveDailyEntry");
  const removeEntry = useOfflineFn("deleteDailyEntry");
  const saveBold = useOfflineFn("saveBoldMove");
  const fetchBoldMoves = useServerFn(listBoldMoves);

  const todayQuery = useQuery({
    queryKey: ["today", businessId, date],
    enabled: Boolean(businessId),
    queryFn: () => fetchToday({ data: { businessId, date, month } }),
  });
  const boldQuery = useQuery({
    queryKey: ["bold-moves", businessId],
    enabled: Boolean(businessId),
    queryFn: () => fetchBoldMoves({ data: { businessId } }),
  });

  const entries = (todayQuery.data?.entries ?? []) as TodayEntry[];

  const [morning, setMorning] = useState("");
  const [whenWhere, setWhenWhere] = useState("");
  const [category, setCategory] = useState<Category | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [savingMorning, setSavingMorning] = useState(false);
  const [bold, setBold] = useState("");
  const composerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setBold(todayQuery.data?.boldMove?.text ?? "");
  }, [todayQuery.data]);

  const showComposer = composerOpen || entries.length === 0;

  const addMove = async () => {
    if (!morning.trim()) {
      toast.error("Write the move first");
      return;
    }
    setSavingMorning(true);
    try {
      await saveEntry({
        data: { businessId, date, morning_text: morning, when_where: whenWhere, category },
      });
      await todayQuery.refetch();
      setMorning("");
      setWhenWhere("");
      setCategory(null);
      setComposerOpen(false);
      toast.success("Locked in for today");
    } finally {
      setSavingMorning(false);
    }
  };

  const submitBold = async () => {
    if (!bold.trim()) return;
    await saveBold({ data: { businessId, month, text: bold.trim() } });
    toast.success("Bold move noted");
    void todayQuery.refetch();
    void boldQuery.refetch();
  };

  if (!businessId) return null;

  const isFirstOfMonth = isFirstOfMonthISO(date);
  const hasBoldThisMonth = Boolean(todayQuery.data?.boldMove?.text);
  const showBoldPrompt = isFirstOfMonth || !hasBoldThisMonth;
  const pastBoldMoves = (boldQuery.data ?? []).filter((m) => m.month !== month);

  const recent = todayQuery.data?.recent ?? [];
  const last14 = Array.from({ length: 14 }, (_, i) => {
    const key = shiftISODate(date, -(13 - i));
    const rows = recent.filter((r) => r.entry_date === key);
    const state = rows.some((r) => r.done) ? "done" : rows.length > 0 ? "logged" : "empty";
    return {
      date: key,
      state,
      text: rows.map((r) => r.morning_text).filter(Boolean).join(" · "),
      count: rows.length,
    };
  });
  const logged = last14.filter((d) => d.state !== "empty").length;
  const kept = last14.filter((d) => d.state === "done").length;

  // Comeback, not reset: a single missed day is forgiven (grace day) instead of
  // sending the run back to zero.
  let streak = 0;
  let graceUsed = false;
  for (let i = last14.length - 1; i >= 0; i--) {
    if (last14[i]?.state === "empty") {
      if (graceUsed) break;
      graceUsed = true;
      continue;
    }
    streak++;
  }
  const missedYesterday = last14[last14.length - 2]?.state === "empty";
  const missedToday = last14[last14.length - 1]?.state === "empty";
  const showComeback = streak > 0 && (missedToday || missedYesterday);

  // Reward real progress, not participation.
  let bestRun = 0;
  let run = 0;
  for (const d of last14) {
    if (d.state === "done") {
      run++;
      bestRun = Math.max(bestRun, run);
    } else {
      run = 0;
    }
  }




  return (
    <div className="space-y-8">
      <WinMoment />
      {entries.map((entry, i) => (
        <MoveCard
          key={entry.id}
          entry={entry}
          index={i}
          onSave={async (patch) => {
            await saveEntry({ data: { id: entry.id, businessId, date, ...patch } });
            await todayQuery.refetch();
          }}
          onDelete={async () => {
            await removeEntry({ data: { id: entry.id } });
            await todayQuery.refetch();
            toast.success("Move removed");
          }}
        />
      ))}

      {entries.length > 0 && !showComposer && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setComposerOpen(true);
              requestAnimationFrame(() =>
                composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
              );
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" /> Add another move
          </Button>
          <Link
            to="/app/history"
            className="inline-flex min-h-9 items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            See the full timeline <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
      )}

      {showComposer && (
        <section ref={composerRef} className="rise spotlight vault-panel p-7">
          <p className="micro-label">
            {entries.length === 0
              ? "Today · 01"
              : `Move ${String(entries.length + 1).padStart(2, "0")}`}
          </p>
          <h2 className="mt-3 text-xl font-medium">
            {entries.length === 0
              ? "What's one thing you'll do today to move this business forward?"
              : "What else will you move forward today?"}
          </h2>
          <Textarea
            className="mt-4 min-h-24 resize-none"
            placeholder="Today I'll…"
            value={morning}
            onChange={(e) => setMorning(e.target.value)}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setCategory(category === c.value ? null : c.value)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  category === c.value
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="mt-5">
            <label className="micro-label" htmlFor="when-where">
              When and where
            </label>
            <Input
              id="when-where"
              className="mt-2"
              placeholder="9:15am, at the desk, before email"
              value={whenWhere}
              onChange={(e) => setWhenWhere(e.target.value)}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Naming a time and a place is the step research links most strongly to actually doing
              it.{" "}
              <Link to="/method" className="underline underline-offset-2 hover:text-foreground">
                Why this works
              </Link>
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button className="sheen" disabled={savingMorning} onClick={() => void addMove()}>
              {savingMorning ? "Saving…" : "Save this move"}
            </Button>
            {entries.length > 0 && (
              <Button variant="ghost" onClick={() => setComposerOpen(false)}>
                Cancel
              </Button>
            )}
          </div>
          {entries.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              One move is enough. Add more only if they're genuinely today's work.
            </p>
          )}
        </section>
      )}

      <section className="rise soft-panel p-7 [--d:180ms]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="micro-label flex flex-wrap items-center gap-2">
              Last 14 days
              {streak > 1 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[0.6rem] font-semibold text-primary">
                  <Flame className="h-3 w-3" aria-hidden="true" /> {streak} day run
                </span>
              )}
              {graceUsed && streak > 1 && (
                <span className="rounded-full border border-border px-2 py-0.5 text-[0.6rem] font-medium text-muted-foreground">
                  one day off, run kept
                </span>
              )}
            </p>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              A record, not a scoreboard. One missed day never sends you back to zero.
            </p>
            {showComeback && (
              <p className="mt-3 max-w-md rounded-sm border border-primary/25 bg-primary/5 px-3 py-2 text-sm text-foreground">
                You missed a day — the run is still yours. Name one small move and pick it right
                back up.
              </p>
            )}

          </div>
          <div className="hidden text-right sm:block">
            <p className="text-2xl font-semibold tabular-nums text-foreground" translate="no">
              {kept}
            </p>
            <p className="text-[0.65rem] text-muted-foreground">of 14 closed</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {last14.map((d) => (
            <span
              key={d.date}
              translate="no"
              title={`${d.date}${d.count ? ` — ${d.count} move${d.count > 1 ? "s" : ""}: ${d.text}` : " — no entry"}`}
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded-full border transition-all duration-500",
                d.state === "done"
                  ? "border-primary/70 bg-primary/40 shadow-[0_0_8px_-3px_var(--color-primary)]"
                  : d.state === "logged"
                    ? "border-primary/50 bg-primary/15"
                    : "border-destructive/40 bg-destructive/10",
              )}
            >
              {d.state === "done" && <Check className="h-3 w-3 text-primary" aria-hidden="true" />}
              {d.state === "logged" && (
                <span className="h-1 w-1 rounded-full bg-primary" aria-hidden="true" />
              )}
              {d.state === "empty" && (
                <span className="h-0.5 w-0.5 rounded-full bg-destructive/70" aria-hidden="true" />
              )}
            </span>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.65rem] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-3 w-3 items-center justify-center rounded-full border border-primary/70 bg-primary/40">
              <Check className="h-2 w-2 text-primary" aria-hidden="true" />
            </span>
            Followed through
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-3 w-3 items-center justify-center rounded-full border border-primary/50 bg-primary/15">
              <span className="h-0.5 w-0.5 rounded-full bg-primary" aria-hidden="true" />
            </span>
            Logged, not done
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-3 w-3 items-center justify-center rounded-full border border-destructive/40 bg-destructive/10">
              <span className="h-0.5 w-0.5 rounded-full bg-destructive/70" aria-hidden="true" />
            </span>
            No entry
          </span>
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          {logged > 0 ? (
            <>
              <span className="text-foreground">
                {kept} {kept === 1 ? "move" : "moves"} finished
              </span>{" "}
              in the last 14 days · {logged} {logged === 1 ? "day" : "days"} active
              {bestRun > 1 ? ` · best run of finished days: ${bestRun}` : ""}.
            </>
          ) : (
            "No entries in the last 14 days."
          )}
        </p>

        {kept > 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            {kept >= 10
              ? "That's real work finished, not just days opened."
              : kept >= 4
                ? "Finishing is the part that counts — you're doing it."
                : "Every finished move counts more than a day just logged."}
          </p>
        )}


      </section>


      {showBoldPrompt && (
        <section className="vault-panel p-7 ring-1 ring-primary/25">
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="h-4 w-4" />
            <span className="micro-label text-primary">Monthly risk</span>
          </div>
          <h2 className="mt-3 text-xl font-medium">
            What's one bold move for this business this month?
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{formatMonth(month)}</p>
          <Textarea
            className="mt-4 min-h-20 resize-none"
            placeholder="This month I'll risk…"
            value={bold}
            onChange={(e) => setBold(e.target.value)}
          />
          <Button className="mt-5" onClick={() => void submitBold()}>
            Save bold move
          </Button>
        </section>
      )}

      {hasBoldThisMonth && !showBoldPrompt && (
        <section className="rise soft-panel p-7 [--d:120ms]">
          <span className="micro-label">This month's bold move</span>
          <p className="notranslate mt-2 leading-relaxed">{todayQuery.data?.boldMove?.text}</p>
        </section>
      )}

      {pastBoldMoves.length > 0 && (
        <section>
          <h3 className="micro-label">Earlier bold moves</h3>
          <ul className="mt-3 space-y-3">
            {pastBoldMoves.map((m) => (
              <li key={m.id} className="soft-panel p-5">
                <p className="text-xs text-muted-foreground">{formatMonth(m.month)}</p>
                <p className="notranslate mt-1 text-sm leading-relaxed">{m.text}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
